import { Select, SelectProps } from 'antd';
import { useState, useMemo } from 'react';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import { useDebounce } from '@/hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ListCompaniesQuery } from '@/core/domain/enterprise/Companies/Companies.types';
import { useNbs, useNbsByCode } from '@/core/domain/invoice/nbs/Nbs.hooks';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListCompaniesQuery;
  queryEnable?: boolean;
};

const InvoiceNBSInfiniteSelect: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState(false);
  const [searchValue, setSearchValue] = useState<string>();

  const search = useDebounce(searchValue);

  const {
    data: nbsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useNbs(
    {
      active: true,
      rows: 30,
      search,
      ...queryFilter,
    },
    (queryEnable || selectClicked) && !props.disabled,
  );

  const { data: nbsDetail } = useNbsByCode(props.value);

  const nbsList = useMemo(() => flatInfiniteData(nbsData), [nbsData]);

  const nbsWithSelected = useMemo(() => {
    if (nbsDetail && !nbsList.some((nbs) => nbs.code === nbsDetail.code)) {
      return [nbsDetail, ...nbsList];
    }
    return nbsList;
  }, [nbsList, nbsDetail]);

  const options = useMemo(() => {
    return nbsWithSelected.map((nbs) => (
      <Select.Option key={nbs.code} value={nbs.code}>
        {`${nbs.code} - ${nbs.description}`}
      </Select.Option>
    ));
  }, [nbsWithSelected]);

  return (
    <InfiniteSelect
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      loading={isLoading}
      children={options}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      allowClear
      onSelect={() => setSelectClicked(false)}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      {...props}
    />
  );
};

export default InvoiceNBSInfiniteSelect;
